/*
 * EMAIL wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component({
    selector: 'reporter-email',
    templateUrl: './email.component.html',
    styleUrls: ['./email.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EmailComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => EmailComponent),
            multi: true
        },
    ],
    standalone: false
})

export class EmailComponent implements ControlValueAccessor, Validator, AfterViewInit {

  @Input() maxEmail: any;

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {
  }

  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChange(v);
    }
  }

  get value() {
    return this.innerValue;
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty('required');
  }

  innerValue: any;
  initialValue: string;
  initialized = false;

  @ViewChild('field', { static: true }) field: ElementRef;
  onChange = (_: any) => {};
  onTouched = () => {};

  ngAfterViewInit() {
    this.initialValue = this.value;

    if (this.required) {
      this.renderer.setAttribute(this.field.nativeElement, 'required', '');
    }
    if (this.elementRef.nativeElement.classList.contains('required')) {
      this.renderer.addClass(this.field.nativeElement, 'required');
    }

    this.initialized = true;
  }

  get maxLength() {
    return this.elementRef.nativeElement.attributes.maxlength ? this.elementRef.nativeElement.attributes.maxlength.value : undefined;
  }

  writeValue(value: any): void {
    this.innerValue = value;
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', value);
    this.onChange(this.innerValue);
    this.onTouched();
  }

  registerOnChange(fn: (value: any) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  validate(control: FormControl): ValidationErrors {
    if (!this.initialized) {
      return null;
    }

    if (!control.value && this.required) {
      return {
        required: true,
        message: ' is required.'
      };
    }

    if (control.value !== this.initialValue) {
      if (control.value && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/.test(control.value)) {
        return {
          patternMismatch: true,
          message: ' must be a valid email address.'
        };
      }

      if (this.maxLength && control.value && control.value.length > this.maxLength) {
        return {
          maxlength: true,
          message: ` must not exceed ${ this.maxLength } characters.`
        };
      }
    }

    return null;
  }
}
